import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import { COMPANY_LOGO_SRC } from '../../constants/branding';

const A4_WIDTH = '8.27in';
const A4_HEIGHT = '11.69in';
/** Inner padding (print-safe margins around the form grid). */
const HBL_INNER_PAD = '10mm 11mm';

/** Last 5 chars of collection number for B/L display (supports alphanumeric). */
const formatHblBlNo = (collectionNo) => {
  const raw = String(collectionNo || '').trim();
  if (!raw) return '—';
  const digits = raw.replace(/\D/g, '');
  if (digits.length >= 5) return digits.padStart(5, '0').slice(-5);
  const alnum = raw.replace(/[^0-9a-zA-Z]/g, '');
  if (alnum.length >= 5) return alnum.slice(-5).toUpperCase();
  return (alnum || raw).padStart(5, '0').toUpperCase().slice(-5);
};

const hblPartyText = (value, fallback = '—') => {
  const s = String(value || '').trim();
  return (s || fallback).toUpperCase();
};

const hblConsigneeId = (person) => {
  const id = person?.passportNo || person?.nicNo || person?.drivingLicenceNo || '';
  return hblPartyText(id);
};

const hblPdfDownloadBase = (collection) => {
  const no = (collection?.collectionNo || '').toString().trim();
  if (no) return `HBL#${no}`.replace(/[^\w#.-]+/g, '_');
  const idTail = `${collection?._id || collection?.id || ''}`.replace(/[^0-9a-f]/gi, '').slice(-8);
  return `HBL#${idTail || 'draft'}`.replace(/[^\w#.-]+/g, '_');
};

const normalizeMobile = (mobiles) => {
  if (Array.isArray(mobiles)) return mobiles.filter(Boolean).join(' / ');
  if (typeof mobiles === 'string') return mobiles;
  return '';
};

/** HBL uses collection date only; avoid UTC day shift for YYYY-MM-DD strings. */
const formatHblDate = (value) => {
  if (value == null || value === '') return '';
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}/.test(value.trim())) {
    return value.trim().slice(0, 10);
  }
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const HBL = ({ collection, shipper, consignee, childCollections = [], onClose, onPrint }) => {
  const rootRef = useRef(null);
  const downloadLock = useRef(false);
  const logoTriedFallback = useRef(false);
  const [logoSrc, setLogoSrc] = useState(COMPANY_LOGO_SRC);
  const [downloadBusy, setDownloadBusy] = useState(false);

  useEffect(() => {
    logoTriedFallback.current = false;
    setLogoSrc(COMPANY_LOGO_SRC);
  }, [collection]);

  const handleDownloadPdf = useCallback(async () => {
    const el = rootRef.current;
    if (!el || downloadLock.current || !collection) return;
    downloadLock.current = true;
    setDownloadBusy(true);
    try {
      const pdf = new jsPDF({ unit: 'mm', format: 'a4', orientation: 'portrait' });
      const pdfW = pdf.internal.pageSize.getWidth();
      const pdfH = pdf.internal.pageSize.getHeight();
      const canvas = await html2canvas(el, {
        scale: 2,
        useCORS: true,
        logging: false,
        backgroundColor: '#ffffff',
        ignoreElements: (node) => node.classList?.contains('hbl-hide-print'),
      });
      const imgData = canvas.toDataURL('image/jpeg', 0.92);
      pdf.addImage(imgData, 'JPEG', 0, 0, pdfW, pdfH, undefined, 'FAST');
      pdf.save(`${hblPdfDownloadBase(collection)}.pdf`);
    } catch (e) {
      console.error(e);
      alert('Could not create PDF. Try Print (A4) and choose Save as PDF instead.');
    } finally {
      downloadLock.current = false;
      setDownloadBusy(false);
    }
  }, [collection]);

  const sourceBoxes = useMemo(() => {
    if (Array.isArray(childCollections) && childCollections.length > 0) {
      return childCollections.flatMap((c) => (Array.isArray(c?.boxes) ? c.boxes : []));
    }
    return Array.isArray(collection?.boxes) ? collection.boxes : [];
  }, [collection?.boxes, childCollections]);

  /** One HBL line per distinct box label (official name, else box type, else description); qty / gross kg / CBM summed. */
  const rows = useMemo(() => {
    if (!sourceBoxes.length) {
      return [{ description: '—', quantity: 1, lineWeightKg: 0, lineCbm: 0 }];
    }

    const mergeMap = new Map();

    sourceBoxes.forEach((box, idx) => {
      const quantity = parseInt(box.quantity, 10) || 1;
      const weight = parseFloat(box.weight) || 0;
      const cbm = parseFloat(box.cbm) || 0;
      const official = (box.officialBoxName || '').trim();
      const boxType = (box.boxType || '').trim();
      const desc = (box.description || '').trim();
      const labelForMerge = official || boxType || desc;
      const lineWeight = weight * quantity;
      const lineCbm = cbm * quantity;

      if (!labelForMerge) {
        mergeMap.set(`__single_${idx}`, {
          description: '—',
          quantity,
          lineWeightKg: lineWeight,
          lineCbm,
        });
        return;
      }

      const key = labelForMerge.toUpperCase();
      const displayLabel = official || boxType || desc;
      const existing = mergeMap.get(key);
      if (!existing) {
        mergeMap.set(key, {
          description: displayLabel,
          quantity,
          lineWeightKg: lineWeight,
          lineCbm,
        });
      } else {
        existing.quantity += quantity;
        existing.lineWeightKg += lineWeight;
        existing.lineCbm += lineCbm;
        if (official) existing.description = official;
      }
    });

    return Array.from(mergeMap.values());
  }, [sourceBoxes]);

  const clearingMark = useMemo(() => {
    const ct = String(collection?.clearingType || '').trim().toUpperCase();
    if (ct.includes('GIFT')) return 'GIFT';
    if (ct.includes('UPB')) return 'UPB';
    if (ct.includes('DOOR')) return 'DOOR TO DOOR';
    return ct || 'UPB';
  }, [collection?.clearingType]);

  const totals = useMemo(() => {
    const totalPackages = rows.reduce((sum, row) => sum + row.quantity, 0);
    const totalWeight = rows.reduce((sum, row) => sum + row.lineWeightKg, 0);
    const totalCBM = rows.reduce((sum, row) => sum + row.lineCbm, 0);
    return { totalPackages, totalWeight, totalCBM };
  }, [rows]);

  const blNo = formatHblBlNo(collection?.collectionNo);
  const formattedDate = useMemo(
    () => formatHblDate(collection?.collectionDate),
    [collection?.collectionDate]
  );

  return (
    <div className="hbl-root" style={{ backgroundColor: '#f3f4f6', padding: '8px 0' }}>
      <style>
        {`
          .hbl-print-sheet {
            width: ${A4_WIDTH};
            height: ${A4_HEIGHT};
            min-height: ${A4_HEIGHT};
            max-width: ${A4_WIDTH};
            margin: 0 auto;
            background: #fff;
            box-sizing: border-box;
            border: none;
            padding: ${HBL_INNER_PAD};
            font-family: Calibri, "Segoe UI", Candara, "Helvetica Neue", Arial, sans-serif;
            font-size: 11px;
            line-height: 1.35;
            color: #000;
            -webkit-font-smoothing: antialiased;
            display: flex;
            flex-direction: column;
          }
          .hbl-page-body {
            flex: 1;
            display: flex;
            flex-direction: column;
            min-height: 0;
          }
          .hbl-page-top {
            flex-shrink: 0;
          }
          .hbl-page-top .hbl-doc-title + .hbl-table tbody tr:first-child td {
            border-top: none;
          }
          .hbl-page-stretch {
            flex: 1;
            display: flex;
            flex-direction: column;
            min-height: 0;
          }
          .hbl-cargo-table-wrap {
            flex: 1;
            min-height: 100px;
            position: relative;
          }
          .hbl-cargo-main-table {
            position: absolute;
            left: 0;
            right: 0;
            top: 0;
            bottom: 0;
            width: 100%;
            height: 100%;
            table-layout: fixed;
          }
          .hbl-cargo-main-table tbody tr.hbl-cargo-fill-row {
            height: 100%;
          }
          .hbl-cargo-main-table tbody tr.hbl-cargo-fill-row td {
            height: 100%;
            vertical-align: top;
          }
          .hbl-footer-table {
            flex-shrink: 0;
            margin-top: 0;
          }
          .hbl-signature-img {
            display: block;
            max-width: 176px;
            max-height: 41.6px;
            width: auto;
            height: auto;
            object-fit: contain;
            object-position: left bottom;
            margin-top: 4px;
          }
          .hbl-table {
            width: 100%;
            border-collapse: collapse;
          }
          .hbl-table td, .hbl-table th {
            border: 1px solid #000;
            vertical-align: top;
            padding: 5px 6px;
          }
          .hbl-doc-title {
            text-align: center;
            font-size: 20px;
            font-weight: 700;
            letter-spacing: 0.08em;
            text-transform: uppercase;
            line-height: 1.25;
            padding: 10px 12px 9px;
            margin: 0;
            box-sizing: border-box;
            border: 1px solid #000;
            border-bottom: 2px solid #000;
          }
          .hbl-small {
            font-size: 10.5px;
            line-height: 1.3;
            font-weight: 700;
            letter-spacing: 0.05em;
            text-transform: uppercase;
            color: #111;
          }
          .hbl-mid {
            font-size: 12px;
            line-height: 1.4;
            font-weight: 600;
          }
          .hbl-addr {
            font-size: 13px;
            line-height: 1.35;
            font-weight: 600;
          }
          .hbl-table thead th {
            font-size: 10.5px;
            line-height: 1.35;
            font-weight: 700;
            letter-spacing: 0.03em;
            text-transform: uppercase;
            background: #f2f2f2;
            padding: 7px 6px;
          }
          .hbl-cargo-desc {
            font-size: 12px;
            font-weight: 600;
            line-height: 1.55;
          }
          .hbl-cargo-num {
            font-size: 12px;
            font-weight: 600;
            line-height: 1.55;
            text-align: center;
          }
          .hbl-total-row td {
            font-size: 13px;
            font-weight: 700;
            letter-spacing: 0.03em;
            text-transform: uppercase;
            text-align: center;
            vertical-align: middle;
            background: #f2f2f2;
            padding: 7px 6px;
            border-top: 2px solid #000;
          }
          .hbl-section-banner {
            font-size: 13px;
            font-weight: 700;
            letter-spacing: 0.05em;
            text-transform: uppercase;
          }
          .hbl-clearing-mark {
            letter-spacing: 0.04em;
          }
          .hbl-bl-clearing-value {
            font-size: 15px;
            font-weight: 700;
            line-height: 1.2;
            letter-spacing: 0.03em;
          }
          .hbl-legal {
            font-size: 10.5px;
            line-height: 1.4;
            font-weight: 700;
            letter-spacing: 0.02em;
            text-transform: uppercase;
          }
          .hbl-party-grid {
            table-layout: fixed;
          }
          .hbl-party-grid td.hbl-party-col {
            border-right: 2px solid #000;
          }
          @media screen and (max-width: 900px) {
            .hbl-root {
              padding: 0 !important;
              overflow-x: auto;
              -webkit-overflow-scrolling: touch;
            }
            .hbl-print-sheet {
              width: 100% !important;
              max-width: 100% !important;
              height: auto !important;
              min-height: unset !important;
              padding: 8mm 6mm !important;
              font-size: 10px;
            }
            .hbl-hide-print {
              max-width: 100% !important;
              padding: 8px 12px !important;
              text-align: center !important;
              box-sizing: border-box;
            }
            .hbl-hide-print button {
              margin: 4px !important;
              font-size: 13px;
              padding: 8px 12px !important;
            }
          }
          @media print {
            * {
              -webkit-print-color-adjust: exact !important;
              print-color-adjust: exact !important;
            }
            html, body {
              margin: 0;
              padding: 0;
              background: #fff;
            }
            @page {
              size: A4;
              margin: 0;
            }
            .hbl-root {
              background: #fff !important;
              padding: 0 !important;
            }
            .hbl-print-sheet {
              margin: 0 auto !important;
              width: ${A4_WIDTH} !important;
              height: ${A4_HEIGHT} !important;
              min-height: ${A4_HEIGHT} !important;
              max-width: ${A4_WIDTH} !important;
              page-break-after: auto;
            }
            .hbl-hide-print {
              display: none !important;
            }
          }
        `}
      </style>

      <div className="hbl-hide-print" style={{ maxWidth: A4_WIDTH, margin: '0 auto', textAlign: 'right', padding: '0 0 10px', boxSizing: 'border-box' }}>
        <button
          type="button"
          onClick={onClose}
          style={{ border: 'none', background: '#374151', color: '#fff', padding: '8px 14px', cursor: 'pointer' }}
        >
          Close
        </button>
        <button
          type="button"
          onClick={onPrint}
          style={{ border: 'none', background: '#111827', color: '#fff', padding: '8px 14px', marginLeft: '8px', cursor: 'pointer' }}
        >
          Print (A4)
        </button>
        <button
          type="button"
          onClick={handleDownloadPdf}
          disabled={downloadBusy}
          style={{
            border: 'none',
            background: downloadBusy ? '#9ca3af' : 'var(--jet-royal, #0056f1)',
            color: '#fff',
            padding: '8px 14px',
            marginLeft: '8px',
            cursor: downloadBusy ? 'wait' : 'pointer',
          }}
        >
          {downloadBusy ? 'Preparing…' : 'Download PDF (A4)'}
        </button>
      </div>

      <div ref={rootRef} className="hbl-print-sheet">
        <div className="hbl-page-body">
          <div className="hbl-page-top">
        <div className="hbl-doc-title">HOUSE BILL OF LADING</div>

        <table className="hbl-table">
          <tbody>
            <tr>
              <td style={{ width: '150px', textAlign: 'center', padding: '6px' }}>
                <img
                  src={logoSrc}
                  alt="Jet Shipping Logo"
                  style={{ width: '110px', height: '80px', objectFit: 'contain' }}
                  onError={() => {
                    if (!logoTriedFallback.current) {
                      logoTriedFallback.current = true;
                      setLogoSrc('/images/logo/jet-shipping-logo.png');
                    }
                  }}
                />
              </td>
              <td style={{ textAlign: 'center' }}>
                <div className="hbl-addr">NO. AZ-B1-07, LOT NO. 1B/2(A), ANZEN BUSINESS PARK,</div>
                <div className="hbl-addr">NO 3-9, JALAN 4/37A, KAWASAN INDUSTRI TAMAN BUKIT MALURI,</div>
                <div className="hbl-addr">52100, KEPONG, KUALA LUMPUR.</div>
                <div style={{ height: 6 }} />
                <div className="hbl-addr">HOTLINE: +60 1121190765, +60 362620505</div>
                <div style={{ height: 6 }} />
                <div className="hbl-addr">EMAIL: JETSHIPPINGANDLOGISTIC@GMAIL.COM</div>
              </td>
            </tr>
          </tbody>
        </table>

        <table className="hbl-table hbl-party-grid">
          <colgroup>
            <col style={{ width: '57%' }} />
            <col style={{ width: '16.5%' }} />
            <col style={{ width: '16.5%' }} />
            <col style={{ width: '10%' }} />
          </colgroup>
          <tbody>
            <tr>
              <td rowSpan="2" className="hbl-party-col" style={{ padding: '4px' }}>
                <div className="hbl-small">SHIPPER</div>
                <div className="hbl-mid">{hblPartyText(shipper?.nameWithInitials)}</div>
                <div className="hbl-mid">{hblPartyText(shipper?.address || 'MALAYSIA', 'MALAYSIA')}</div>
              </td>
              <td colSpan="2" style={{ padding: '4px' }}>
                <div className="hbl-small">B/L NO</div>
                <div className="hbl-bl-clearing-value">{blNo}</div>
              </td>
              <td style={{ padding: '4px', textAlign: 'center', verticalAlign: 'middle' }} className="hbl-clearing-mark">
                <span className="hbl-bl-clearing-value">
                  {clearingMark}
                </span>
              </td>
            </tr>
            <tr>
              <td colSpan="3" style={{ padding: '4px' }}>
                <div className="hbl-small">VESSEL / VOYAGE NO</div>
                <div className="hbl-mid">/</div>
              </td>
            </tr>
            <tr>
              <td rowSpan="3" className="hbl-party-col" style={{ padding: '4px' }}>
                <div className="hbl-small">CONSIGNEE</div>
                <div className="hbl-mid">{hblPartyText(consignee?.nameWithInitials)}</div>
                <div className="hbl-mid">{hblPartyText(consignee?.address)}</div>
                <div className="hbl-mid">TEL:{normalizeMobile(consignee?.mobiles) || '—'}</div>
                <div className="hbl-mid">ID/PASSPORT: {hblConsigneeId(consignee)}</div>
              </td>
              <td colSpan="3" style={{ padding: '4px' }}>
                <div className="hbl-small">PORT OF RECEIPT</div>
                <div className="hbl-mid">PORT KLANG</div>
              </td>
            </tr>
            <tr>
              <td colSpan="3" style={{ padding: '4px' }}>
                <div className="hbl-small">PLACE OF DELIVERY</div>
                <div className="hbl-mid">COLOMBO</div>
              </td>
            </tr>
            <tr>
              <td colSpan="3" style={{ padding: '4px' }}>
                <div className="hbl-small">PORT OF LOADING</div>
                <div className="hbl-mid">PORT KLANG</div>
              </td>
            </tr>
            <tr>
              <td rowSpan="2" className="hbl-party-col" style={{ padding: '4px', verticalAlign: 'top' }}>
                <div className="hbl-small">NOTIFY PARTY</div>
                <div className="hbl-mid">SAME AS CONSIGNEE</div>
              </td>
              <td colSpan="3" style={{ padding: '4px' }}>
                <div className="hbl-small">PORT OF DISCHARGE</div>
                <div className="hbl-mid">COLOMBO</div>
              </td>
            </tr>
            <tr>
              <td colSpan="3" style={{ padding: '4px' }}>
                <div className="hbl-small">NO OF ORIGINAL B/L'S</div>
                <div className="hbl-mid">1 OF 1</div>
              </td>
            </tr>
            <tr>
              <td colSpan="4" style={{ textAlign: 'center', padding: '6px', background: '#f2f2f2', borderTop: '2px solid #000' }} className="hbl-section-banner">
                PARTICULARS FURNISHED BY SHIPPER
              </td>
            </tr>
          </tbody>
        </table>
          </div>

          <div className="hbl-page-stretch">
            <div className="hbl-cargo-table-wrap">
              <table className="hbl-table hbl-cargo-main-table">
                <thead>
                  <tr>
                    <th style={{ width: '20%', textAlign: 'center' }}>CONTAINER NO &amp; SEAL NO.<br />MARKS &amp; NOS</th>
                    <th style={{ width: '37%', textAlign: 'center' }}>DESCRIPTION OF GOODS</th>
                    <th style={{ width: '13%', textAlign: 'center' }}>QUANTITY &amp; KIND<br />OF PACKAGES</th>
                    <th style={{ width: '15%', textAlign: 'center' }}>GROSS WEIGHT (KG)</th>
                    <th style={{ width: '15%', textAlign: 'center' }}>MEASUREMENTS<br />(CBM)</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="hbl-cargo-fill-row">
                    <td />
                    <td className="hbl-cargo-desc">
                      {rows.map((row, idx) => (
                        <div key={`desc-${idx}`}>{row.description.toUpperCase()}</div>
                      ))}
                    </td>
                    <td className="hbl-cargo-num">
                      {rows.map((row, idx) => (
                        <div key={`qty-${idx}`}>{row.quantity}</div>
                      ))}
                    </td>
                    <td className="hbl-cargo-num">
                      {rows.map((row, idx) => (
                        <div key={`w-${idx}`}>{row.lineWeightKg.toFixed(0)}</div>
                      ))}
                    </td>
                    <td className="hbl-cargo-num">
                      {rows.map((row, idx) => (
                        <div key={`c-${idx}`}>{row.lineCbm.toFixed(2)}</div>
                      ))}
                    </td>
                  </tr>
                  <tr className="hbl-total-row">
                    <td colSpan="2">TOTAL</td>
                    <td>{totals.totalPackages}</td>
                    <td>
                      {Math.round(totals.totalWeight)} KG
                    </td>
                    <td>
                      {totals.totalCBM.toFixed(2)} CBM
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <table className="hbl-table hbl-footer-table">
              <tbody>
                <tr>
                  <td colSpan="2" style={{ minHeight: '120px', padding: '8px', verticalAlign: 'top' }}>
                    <div className="hbl-legal">COMPANY IS NOT LIABLE FOR ANY DAMAGES OR LOSSES ON TRANSIT UNLESS THE CUSTOMER REQUEST FOR MARINE INSURANCE COVER.</div>
                    <div style={{ height: '12px' }} />
                    <div className="hbl-legal">AS AGENT ONLY:</div>
                    <div style={{ marginTop: '8px' }}>
                      <div className="hbl-mid">SIGNATURE:</div>
                      <img
                        src="/images/hbl-signature.png"
                        alt="Authorized signature"
                        className="hbl-signature-img"
                      />
                    </div>
                    <div style={{ marginTop: '10px' }} className="hbl-mid">
                      DATE: {formattedDate || '—'}
                    </div>
                  </td>
                  <td colSpan="3" style={{ minHeight: '120px', padding: '8px', verticalAlign: 'top' }}>
                    <div className="hbl-mid">DELIVERY AGENT:</div>
                    <div className="hbl-mid">WORLDWIDE TRIGO CARGO (PVT) LTD</div>
                    <div className="hbl-mid">NO. 272, GANEMULLA ROAD, MAELIYA, JA ELA, SRI LANKA. 11350</div>
                    <div style={{ marginTop: '10px' }} className="hbl-mid">TEL: 0777615888 / 0715885997</div>
                    <div style={{ marginTop: '10px' }} className="hbl-mid">EMAIL: SERVICE@TRIGOCARGO.COM</div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HBL;
